import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';

const statTileValueVariants = cva('font-mono', {
	variants: {
		tone: {
			primary: 'text-text',
			accent: 'text-accent',
			purple: 'text-accent-purple',
			success: 'text-accent-green',
			warn: 'text-warn',
			danger: 'text-danger',
			muted: 'text-text-muted',
		},
		appearance: {
			inset: 'text-sm font-bold',
			card: 'text-base font-extrabold',
		},
	},
	defaultVariants: {
		tone: 'primary',
		appearance: 'inset',
	},
});

export interface StatTileProps {
	/** Etichetta maiuscola sopra il valore. */
	label: React.ReactNode;
	/** Valore mostrato in monospazio. */
	value: React.ReactNode;
	/** Nota piccola sotto il valore. */
	hint?: React.ReactNode;
	/** Colore del valore. Default: `primary`. */
	tone?: VariantProps<typeof statTileValueVariants>['tone'];
	/** Stile del riquadro: `inset` (incassato) o `card` (con bordo e sfondo card). Default: `inset`. */
	appearance?: VariantProps<typeof statTileValueVariants>['appearance'];
	/** Classi CSS aggiuntive per il riquadro. */
	className?: string;
	/**
	 * Azione in testata, es. l'esploso per POD. Sta accanto all'etichetta e non
	 * sotto al valore: il riquadro è compatto e una riga in più ne romperebbe
	 * l'allineamento con gli altri della griglia.
	 */
	action?: React.ReactNode;
}

/** Riquadro statistica compatto: etichetta maiuscola sopra un valore monospaziato. Da disporre dentro una {@link Grid}. */
const StatTile = ({
	label,
	value,
	hint,
	tone,
	appearance = 'inset',
	className,
	action,
}: StatTileProps) => (
	<div
		className={cn(
			appearance === 'card'
				? 'rounded-xl border border-border bg-card p-3 text-center'
				: 'rounded-lg border border-border bg-background/50 p-3',
			className,
		)}
	>
		<div
			className={cn(
				'flex items-center gap-1 text-[11px] uppercase text-text-muted',
				appearance === 'card' ? 'justify-center tracking-wider' : 'justify-between',
			)}
		>
			{label}
			{action}
		</div>
		<div className={statTileValueVariants({ tone, appearance })}>{value}</div>
		{hint && (
			<div
				className={cn(
					'mt-0.5 text-text-muted',
					appearance === 'card' ? 'text-[12px]' : 'text-[11px]',
				)}
			>
				{hint}
			</div>
		)}
	</div>
);
StatTile.displayName = 'StatTile';

const COMPONENT_KPI_TONE = {
	accent: 'text-accent',
	success: 'text-accent-green',
	warn: 'text-warn',
	purple: 'text-accent-purple',
} as const;

export interface ComponentKpiProps {
	/** Etichetta della componente. */
	label: React.ReactNode;
	/** Valore mostrato in monospazio. */
	value: React.ReactNode;
	/** Quota percentuale sul totale (0-100), determina la barretta. */
	pct: number;
	/** Colore del riquadro. */
	tone: keyof typeof COMPONENT_KPI_TONE;
}

/** Riquadro compatto di scomposizione: etichetta, valore monospaziato e barretta di quota (es. componenti di un incentivo). */
const ComponentKpi = ({ label, value, pct, tone }: ComponentKpiProps) => (
	<div className="rounded-lg border border-border/60 p-3">
		<div className="text-[11px] text-text-muted">{label}</div>
		<div className={cn('font-mono text-sm font-bold', COMPONENT_KPI_TONE[tone])}>{value}</div>
		<div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-border">
			<div
				className={cn('h-full rounded-full bg-current', COMPONENT_KPI_TONE[tone])}
				style={{ width: `${Math.min(pct, 100)}%` }}
			/>
		</div>
		<div className="mt-0.5 text-right font-mono text-[11px] text-text-muted">{pct}%</div>
	</div>
);
ComponentKpi.displayName = 'ComponentKpi';

export { ComponentKpi, StatTile, statTileValueVariants };
